'use client';

import React, { useId, useMemo, useRef, useState } from 'react';
import { cn } from '../../utilities/classname';
import { ExtractPageBlock } from '../render-page/types/block.types';
import { buttonCva } from '../utils/button-classnames';
import { isString } from '../../utilities/is-something';
import { X } from 'lucide-react';
import FormView from './form-view';
import useSubmitFormSubmission from '../../utilities/hooks/use-submit-form-submission';
import { Portal } from './portal';
import { inputCva } from '../utils/input-classnames';
import { trackClick } from '../../utilities/track-click';
import { useAnalyticsTenantId } from './analytics-context';
import { isBookingMode, resolveBookingPage } from '../../utilities/forms/booking-page';
import { BookingButton } from './booking-button';

export type FormButtonBlock = ExtractPageBlock<'form-button'>;

export const FormButtonBlockView: React.FC<{
  block: FormButtonBlock;
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'lg';
  /** Server-rendered label (with prepend/append icons). Falls back to plain text. */
  label?: React.ReactNode;
}> = ({
  block,
  onClick,
  className,
  size,
  label,
}) => {
    const id = useId();
    const dialogRef = useRef<HTMLDialogElement>(null);
    const analyticsTenantId = useAnalyticsTenantId();

    const form = (block.form && !isString(block.form)) ? block.form : undefined;
    const blockRedirectTo = (block.redirectTarget && !isString(block.redirectTarget))
      ? (block.redirectTarget.slug ? `/${block.redirectTarget.slug}` : '/')
      : undefined;
    const [result, action, isPending] = useSubmitFormSubmission({ form, redirectOverride: blockRedirectTo });
    const [defaultValues, setDefaultValues] = useState<Record<string, string>>({});
    const fixedFields = useMemo(() => {
      const map: Record<string, string> = {};
      for (const entry of block.fixedFields ?? []) {
        if (entry.fieldType && entry.value) map[entry.fieldType] = entry.value;
      }
      return Object.keys(map).length > 0 ? map : undefined;
    }, [block.fixedFields]);

    if (!form) { return null; }

    // A booking Form opens its Booking Page instead of the field modal, so
    // `prependInput`, `fixedFields` and the modal overrides never apply to it.
    // One with no Booking Page (its link is empty) shows nothing, never fields.
    if (isBookingMode(form.integrationMode)) {
      const bookingPage = resolveBookingPage(form);
      if (!bookingPage) { return null; }
      return (
        <BookingButton
          form={form}
          bookingPage={bookingPage}
          // `text` is required on the block, so only a legacy row has none,
          // and `BookingButton` then reads its fallback text.
          text={block.text}
          label={label}
          className={cn(buttonCva({ ...block['button-theme'], size }), className)}
          onClick={onClick}
        />
      );
    }

    const openDialog = () => {
      if (analyticsTenantId) {
        trackClick(analyticsTenantId, 'form_button', form.name ?? form.id, {
          label: block.text ?? undefined,
          formName: form.name ?? undefined,
        });
      }
      onClick?.();
      dialogRef.current?.showModal();
    };

    // When rendering as an inline form, btn classes should not be on the <form> element
    const formClassName = className?.split(/\s+/).filter((c) => !c.startsWith('btn')).join(' ') || undefined;

    const renderButton = () => {

      if (block.prependInput) {
        const field = form.fields?.find((f) => !f.type || !fixedFields?.[f.type]);
        const fieldType = field?.type;

        if (field && fieldType) {
          const renderInput = () => {
            const options = field.options?.filter(Boolean);

            if (options && options.length > 0) {
              return (
                <select
                  id={`input-${id}`}
                  required={!!field.required}
                  name={fieldType}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select...
                  </option>
                  {options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              );
            }

            switch (fieldType) {
              case 'email': return (
                <input type="email" placeholder="mail@site.com" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
              case 'message': return (
                <input type="text" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
              case 'name': return (
                <input type="text" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
              case 'phone': return (
                <input type="tel" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
              case 'website': return (
                <input type="text" placeholder="yourwebsite.com" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
              case 'companyName': return (
                <input type="text" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
              case 'industry': return (
                <input type="text" id={`input-${id}`} required={!!field.required} name={fieldType} />
              );
            }
          };

          return (
            <form className={cn('join w-full', formClassName)} onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              const fieldValue = data.get(fieldType)?.toString() ?? '';
              setDefaultValues({ [fieldType]: fieldValue });
              openDialog();
            }}>
              <label className={cn(inputCva({ size }), 'join-item text-base-content')}>
                {renderInput()}
              </label>

              <button type="submit" className={cn('btn', buttonCva({ ...block['button-theme'], size }), 'join-item')}>
                {label ?? block.text}
              </button>
            </form>
          );
        }
      }

      return (
        <button type="button" className={cn(buttonCva({ ...block['button-theme'], size }), className)} onClick={openDialog}>
          {label ?? block.text}
        </button>
      );
    };

    return (
      <>
        {renderButton()}

        <Portal>
          <dialog ref={dialogRef} className="modal bg-base-content/50 backdrop-blur-xs">
            <div className="modal-box">
              <form method="dialog">
                <button className="btn btn-sm btn-ghost absolute right-2 top-2 btn-square">
                  <X />
                  <span className="sr-only">Close</span>
                </button>
              </form>

              <h2 className="font-bold text-lg">
                {block.modal?.heading || form.modalDefaults?.heading}
              </h2>

              <p className="py-4">
                {block.modal?.text || form.modalDefaults?.text}
              </p>

              {result?.status === 'success' ? (
                <div className="alert alert-success">
                  {block.modal?.successText || form.modalDefaults?.successText || 'Submitted successfully.'}
                </div>
              ) : null}

              <FormView
                form={form}
                isPending={isPending}
                action={action}
                defaultValues={defaultValues}
                fixedFields={fixedFields}
              />
            </div>
          </dialog>
        </Portal>
      </>
    );
  };
